/**
 * Login Page
 *
 * Email + password login with email pre-fill from localStorage
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, AlertCircle, Mail } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import PasswordInput from '../../components/auth/PasswordInput';

// User-friendly error messages
const ERROR_MESSAGES = {
  'Invalid login credentials': 'Incorrect email or password. Please try again.',
  'Email not confirmed': 'Please verify your email address before logging in.',
  'User already registered': 'This email is already registered. Try logging in.',
  'Invalid email': 'Please enter a valid email address.',
  'Signup disabled': 'Registration is currently disabled. Please contact admin.',
  'Email rate limit exceeded': 'Too many attempts. Please try again in a few minutes.',
};

const getErrorMessage = (error) => {
  if (!error) return null;
  for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
    if (error.includes(key)) return value;
  }
  return error;
};

export function Login() {
  const { signIn, loading: authLoading, isAuthenticated, userProfile } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailPrefilled, setEmailPrefilled] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && userProfile?.user_type) {
      const userType = userProfile.user_type;
      if (userType === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (userType === 'buyer') {
        navigate('/buyer/dashboard', { replace: true });
      } else {
        navigate('/seller/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, userProfile, navigate]);

  // Pre-fill email from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('pharmunity_last_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setEmailPrefilled(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: signInError } = await signIn(email, password);

      if (signInError) {
        setError(getErrorMessage(signInError.message));
      }
      // If successful, useAuth will redirect
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-md mx-auto">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-5 h-5" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-white border border-gray-200 flex items-center justify-center mx-auto mb-4 overflow-hidden shadow-sm">
              <img
                src="/logo.png"
                alt="Pharmunity"
                className="h-18 w-18 object-cover scale-[1.35]"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Sign in to Pharmunity
            </h1>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus={!emailPrefilled}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Password */}
            <PasswordInput
              value={password}
              onChange={setPassword}
              label="Password"
              placeholder="Enter your password"
              autoComplete="current-password"
              showRequirements={false}
              className="mb-2"
            />

            {/* Forgot Password */}
            <div className="text-right mb-6">
              <Link
                to="/auth/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Don't have an account?
              </span>
            </div>
          </div>

          {/* Register Link */}
          <Link
            to="/register"
            className="w-full py-3 px-4 border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors flex items-center justify-center gap-2"
          >
            Create an account
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-sm text-gray-500">
        <p>
          By signing in, you agree to our{' '}
          <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
        </p>
        <p className="mt-2">
          <button
            type="button"
            onClick={() => {
              if (confirm('This will clear all cached data and log you out. Continue?')) {
                localStorage.clear();
                sessionStorage.clear();
                document.cookie.split(";").forEach(c => {
                  document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                });
                window.location.reload();
              }
            }}
            className="text-gray-400 hover:text-gray-600 hover:underline"
          >
            Having trouble? Clear cache
          </button>
        </p>
      </footer>
    </div>
  );
}

export default Login;
